import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import {
	Box,
	List,
	ListItem,
	ListItemText,
	IconButton,
	Typography,
	Button,
	CircularProgress,
	Snackbar,
	Alert,
} from "@mui/material";
import ModalImport from "./ModalImport";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ModalEdit from "./ModalEdit";
import { obtenerDatos, eliminarDato, auditPost } from "../../api/config";
import { Create } from "./ModalCreate";
import Swal from "sweetalert2";
import { GlobalContext } from "../../GlobalContext";

const Edit = () => {
	const { t } = useTranslation();
	const { isGranted, getModuleId, site_id } = useContext(GlobalContext);
	const editPermission = isGranted("Configuración", "Editar Contrato", "Editar contrato");
	const deletePermission = isGranted("Configuración", "Editar Contrato", "Eliminar contrato");

	// UI state
	const [showList, setShowList] = useState(false);
	const [contratos, setContratos] = useState([]);
	const [selectedContrato, setSelectedContrato] = useState({});
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [openCreate, setOpenCreate] = useState(false);
	const [openImport, setOpenImport] = useState(false);
	// Loading states
	const [isLoading, setIsLoading] = useState(false);
	const [isRefreshing, setIsRefreshing] = useState(false);
	const [snack, setSnack] = useState({
		open: false,
		type: "info", // "info" | "success" | "error"
		message: "",
		loading: false,
	});
	const showSnack = ({ type = "info", message = "", loading = false }) => {
		setSnack({ open: true, type, message, loading });
	};

	const closeSnack = () => setSnack((s) => ({ ...s, open: false, loading: false }));

	const handleImporting = () => {
		showSnack({
			type: "info",
			loading: true,
			message: t("modal_import.snackbar.importing"),
		});
	};

	const handleImported = async ({ count, message } = {}) => {
		setOpenImport(false);
		setIsRefreshing(true);

		await handleAddContract();

		showSnack({
			type: "success",
			loading: false,
			message: message || t("config.edit_contract.import_success", { count: count ?? 0 }),
		});
	};

	const handleImportError = () => {
		closeSnack();
	};

	// on mount: load contratos
	useEffect(() => {
		const fetchContratos = async () => {
			setIsLoading(true);
			try {
				if (site_id !== null) {
					const datos = await obtenerDatos(`/contracts/get/${site_id}`);
					setContratos(datos);
					setShowList(true);
				}
			} catch (error) {
				console.error("Error al obtener contratos:", error);
			} finally {
				setIsLoading(false);
			}
		};
		fetchContratos();
	}, [site_id]);

	// delete handler
	const handleDelete = (key) => {
		const contrato = contratos[key];
		const id = contrato.id;
		const moduleId = getModuleId("Configuración", "Editar Contrato", "Eliminar contrato");

		Swal.fire({
			title: t("aux.messages.sure"),
			text: t("aux.messages.not_revert"),
			icon: "warning",
			showCancelButton: true,
			confirmButtonColor: "#3085d6",
			cancelButtonColor: "#d33",
			confirmButtonText: t("aux.buttons.yes"),
		}).then((res) => {
			if (res.isConfirmed) {
				eliminarDato("/contracts/delete", id, site_id);
				auditPost(moduleId, site_id);

				Swal.fire({
					title: t("aux.messages.alert.delete"),
					text: t("aux.messages.alert.text_alert.delete_contract"),
					icon: "success",
				}).then(() => {
					setContratos((prev) => {
						const copy = { ...prev };
						delete copy[key];
						return copy;
					});
				});
			}
		});
	};

	// open modals
	const handleOpenModalCreate = () => {
		setSelectedContrato({});
		setOpenCreate(true);
	};

	const handleOpenModalImport = () => {
		setOpenImport(true);
	};

	const handleOpenModal = (key) => {
		setSelectedContrato(contratos[key]);
		setIsModalOpen(true);
	};

	// close both and refresh list with spinner item
	const handleCloseModal = async () => {
		setOpenImport(false);
		setIsModalOpen(false);
		setOpenCreate(false);
		setIsRefreshing(true);
	};
	const handleAddContract = async () => {
		try {
			const datos = await obtenerDatos(`/contracts/get/${site_id}`);
			setContratos(datos);
		} catch (err) {
			console.error(err);
		} finally {
			setIsRefreshing(false);
		}
	};

	return (
		<>
			<Box
				sx={{ width: "100%", p: 2, display: "flex", flexDirection: "column", alignItems: "center" }}
			>
				<Typography variant="h5" gutterBottom>
					{t("config.edit_contract.title")}
				</Typography>
				<Box sx={{ p: 2, width: "75%" }}>
					{isLoading ? (
						<Box sx={{ display: "flex", justifyContent: "center" }}>
							<CircularProgress />
						</Box>
					) : (
						showList && (
							<Box
								sx={{
									mt: 2,
									width: "100%",
									display: "flex",
									flexDirection: "column",
									alignItems: "center",
								}}
							>
								<Box
									sx={{
										width: "100%",
										height: "50vh",
										px: 3,
										py: 1,
										border: 1,
										borderColor: "secondary.main",
										borderRadius: 1,
										overflowY: "auto",
									}}
								>
									<List>
										{Object.keys(contratos).map((key) => (
											<ListItem key={key} sx={{ border: 1, borderColor: "secondary.main", mb: 1 }}>
												<IconButton disabled={!editPermission} onClick={() => handleOpenModal(key)}>
													<EditIcon />
												</IconButton>
												<ListItemText primary={contratos[key].nombre} sx={{ textAlign: "center" }} />
												<IconButton disabled={!deletePermission} onClick={() => handleDelete(key)}>
													<DeleteIcon color="error" />
												</IconButton>
											</ListItem>
										))}
										{isRefreshing && (
											<ListItem sx={{ justifyContent: "center" }}>
												<CircularProgress size={24} />
											</ListItem>
										)}
									</List>
								</Box>
								<Box sx={{ display: "flex", gap: 1, justifyContent: "space-between" }}>
									<Button variant="outlined" color="primary" sx={{ mt: 2 }} onClick={handleOpenModalImport}>
										{t("config.edit_contract.import")}
									</Button>
									<Button variant="contained" color="primary" sx={{ mt: 2 }} onClick={handleOpenModalCreate}>
										{t("config.edit_contract.add")}
									</Button>
								</Box>
							</Box>
						)
					)}
				</Box>
			</Box>
			<ModalEdit
				open={isModalOpen}
				onClose={handleCloseModal}
				contractData={selectedContrato}
				onCreated={handleAddContract}
			/>
			<Create open={openCreate} onClose={handleCloseModal} onCreated={handleAddContract} />
			<ModalImport
				open={openImport}
				onClose={handleCloseModal}
				onImporting={handleImporting}
				onImported={handleImported}
				onImportError={handleImportError}
			/>

			<Snackbar
				open={snack.open}
				onClose={() => setSnack((s) => ({ ...s, open: false }))}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
				autoHideDuration={snack.loading ? null : 2500}
			>
				<Alert
					severity={snack.type === "success" ? "success" : "info"}
					icon={
						snack.loading ? (
							<CircularProgress size={18} />
						) : snack.type === "success" ? (
							<CheckCircleIcon />
						) : undefined
					}
					sx={{ borderRadius: "999px", alignItems: "center", px: 2 }}
				>
					{snack.message}
				</Alert>
			</Snackbar>
		</>
	);
};

export default Edit;
